import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  CircularProgress,
  Alert,
  Button,
  useTheme,
  alpha,
  useMediaQuery,
} from "@mui/material";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import PublicMenuServices from "../../services/PublicMenuService";
import OfferServices from "../../services/OfferService";
import MobileFilterDrawer from "./components/MobileFilterDrawer";
import MenuItemsSection from "./components/MenuItemsSection";
import FloatingCartButton from "./components/FloatingCartButton";
import CartDrawer from "./components/CartDrawer";
import AddToCartDialog from "./components/AddToCartDialog";
import DesktopCartPanel from "./components/DesktopCartPanel";
import MenuHeaderBar from "./components/MenuHeaderBar";
import CategoryNavigationBar from "./components/CategoryNavigationBar";
import CustomerLookupDialog from "./components/CustomerLookupDialog";
import OffersDialog from "./components/OffersDialog";
import RatingDialog from "./components/RatingDialog";
import CompanyInfoDialog from "./components/CompanyInfoDialog";
import { toast } from "react-toastify";
import { useUserWithCompany } from "../../hooks/useCompany";
import CompanyServices from "services/CompanyService";
import RestaurantRatingServices from "services/RestaurantRatingService";
import CustomerServices from "services/CustomerService";
import { isValidPhoneNumber, sanitizePhoneNumber } from "../../utils/phoneUtils";
import { useOptionalGuestSession } from "contexts/GuestSessionContext";
import { withGuestSearch, normalizeGuestType, resolveGuestLandingPath } from "data/guestPortalDefaults";
import { DUMMY_COMPANY, DUMMY_MENU, DUMMY_OFFERS, withTimeout } from "data/guestPortalDummyData";
import { LUXURY, getMenuCartChrome } from "data/guestLuxuryTheme";

const RestaurantMenu = ({ embedded = false, guestMode }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const guestSession = useOptionalGuestSession();
  const urlParams = new URLSearchParams(location.search);
  const urlCompanyId = urlParams.get("companyId");
  const urlTableId = urlParams.get("tableId");
  const isGuestEmbedded = embedded && !!guestMode;

  const { companyId: reduxCompanyId } = useUserWithCompany();
  const companyId = urlCompanyId || reduxCompanyId;
  const isPublicMode = !!urlCompanyId;
  const useLuxuryMenu = isPublicMode || isGuestEmbedded;
  const [menuData, setMenuData] = useState(DUMMY_MENU);
  const [offerData, setOfferData] = useState(DUMMY_OFFERS);
  const [companyData, setCompanyData] = useState(DUMMY_COMPANY);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [dietaryFilter, setDietaryFilter] = useState([]);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // Cart states
  const [cartItems, setCartItems] = useState([]);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [addToCartDialogOpen, setAddToCartDialogOpen] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);

  const cartChrome = getMenuCartChrome(useLuxuryMenu);
  const CART_BTN_GRADIENT = cartChrome.gradient;
  const CART_BTN_GRADIENT_HOVER = cartChrome.gradientHover;
  const CART_BTN_SHADOW = cartChrome.shadow;
  const CART_BTN_SHADOW_HOVER = cartChrome.shadowHover;
  const CART_ACCENT = cartChrome.accent;

  // Rating dialog
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [offersModalOpen, setOffersModalOpen] = useState(false);
  const [companyInfoOpen, setCompanyInfoOpen] = useState(false);
  const [ratingForm, setRatingForm] = useState({
    customerName: "",
    customerPhone: "",
    rating: 0,
    review: "",
    hoverRating: 0,
  });
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  // Customer lookup states (phone capture on menu entry)
  const [customerPromptOpen, setCustomerPromptOpen] = useState(false);
  const [customerMobileInput, setCustomerMobileInput] = useState("");
  const [customerLookupLoading, setCustomerLookupLoading] = useState(false);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [customerSkipped, setCustomerSkipped] = useState(false);
  const [enteredMobile, setEnteredMobile] = useState("");
  const [customerPromptActionDone, setCustomerPromptActionDone] = useState(false);
  const [offersAutoShownAfterPrompt, setOffersAutoShownAfterPrompt] = useState(false);
  const isCustomerMobileInvalid =
    customerMobileInput.length > 0 && !isValidPhoneNumber(customerMobileInput);
  const isRatingPhoneInvalid =
    ratingForm.customerPhone.trim().length > 0 &&
    !isValidPhoneNumber(ratingForm.customerPhone);

  useEffect(() => {
    if (companyInfoOpen && companyId) {
      setReviewsLoading(true);
      RestaurantRatingServices.getByCompany({ companyId, page: 0, limit: 20 })
        .then((res) => {
          if (res?.data?.view?.data) setReviews(res.data.view.data);
        })
        .catch(() => { })
        .finally(() => setReviewsLoading(false));
    }
  }, [companyInfoOpen, companyId]);

  useEffect(() => {
    if (!embedded && urlCompanyId && urlTableId) {
      return;
    }
    fetchMenuData();
    fetchCompanyData();
    fetchOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isGuestEmbedded) return;
    if (!loading && !error && !customerProfile && !customerSkipped) {
      setCustomerPromptOpen(true);
    }
  }, [loading, error, customerProfile, customerSkipped, isGuestEmbedded]);

  useEffect(() => {
    if (isGuestEmbedded) return;
    if (!customerPromptActionDone || offersAutoShownAfterPrompt) return;

    const hasActiveOffers = offerData.some((o) => o.isActive);
    if (hasActiveOffers) {
      setOffersModalOpen(true);
      setOffersAutoShownAfterPrompt(true);
    }
  }, [customerPromptActionDone, offersAutoShownAfterPrompt, offerData, isGuestEmbedded]);

  const handleSkipCustomerLookup = () => {
    setCustomerSkipped(true);
    setCustomerPromptActionDone(true);
    setCustomerPromptOpen(false);
  };

  const handleCustomerLookup = async () => {
    const mobile = sanitizePhoneNumber(customerMobileInput);

    if (!mobile) {
      toast.warning("Please enter a mobile number or skip");
      return;
    }

    if (!isValidPhoneNumber(mobile)) {
      toast.warning("Please enter a valid mobile number (10 to 15 digits)");
      return;
    }

    if (!companyId) {
      toast.error("Company information is missing");
      return;
    }

    try {
      setCustomerLookupLoading(true);
      const response = await CustomerServices.getByMobile(companyId, mobile);

      const list = response?.data?.view?.data || response?.data?.view || [];
      const matchedCustomer = Array.isArray(list)
        ? list.find((c) => String(c.mobile || "") === mobile)
        : null;

      setEnteredMobile(mobile);

      if (matchedCustomer) {
        setCustomerProfile(matchedCustomer);
        toast.success(`Welcome back, ${matchedCustomer.name || "Customer"}!`);
      } else {
        setCustomerProfile({
          name: "Guest",
          mobile,
          Loyalty: null,
          Orders: [],
          isGuestOnly: true,
        });
        toast.info("Number saved. Continue as guest customer.");
      }

      setCustomerPromptActionDone(true);
      setCustomerPromptOpen(false);
    } catch (lookupError) {
      console.error("Customer lookup error:", lookupError);
      toast.error("Unable to check customer right now. You can skip and continue.");
    } finally {
      setCustomerLookupLoading(false);
    }
  };

  const fetchMenuData = async () => {
    try {
      setError(null);

      const response = await withTimeout(
        PublicMenuServices.getMenuByCompanyId(companyId)
      );

      if (response && Array.isArray(response.data?.view) && response.data.view.length > 0) {
        const sortedData = response.data.view.sort(
          (a, b) => a.displayOrder - b.displayOrder,
        );
        setMenuData(sortedData);
      } else {
        setMenuData(DUMMY_MENU);
      }
    } catch (err) {
      console.error("Error fetching menu data:", err);
      setMenuData(DUMMY_MENU);
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const response = await withTimeout(CompanyServices.getById(companyId));

      if (response && response.data?.view?.name) {
        setCompanyData(response.data.view);
      } else {
        setCompanyData(DUMMY_COMPANY);
      }
    } catch (err) {
      console.error("Error fetching company data:", err);
      setCompanyData(DUMMY_COMPANY);
    }
  };

  const fetchOffers = async () => {
    try {
      const response = await withTimeout(OfferServices.getByCompanyId(companyId));

      if (response && response.data?.view?.data) {
        const data = response.data.view.data;
        setOfferData(Array.isArray(data) && data.length ? data : DUMMY_OFFERS);
      } else {
        setOfferData(DUMMY_OFFERS);
      }
    } catch (err) {
      console.error("Error fetching offers:", err);
      setOfferData(DUMMY_OFFERS);
    }
  };

  // Cart Functions
  const handleAddToCartClick = (item) => {
    setSelectedMenuItem(item);
    setAddToCartDialogOpen(true);
  };

  const handleAddToCartConfirm = (cartItem) => {
    // Normalize the cart item structure to prevent double nesting
    const normalizedCartItem = {
      menuItem: cartItem.menuItem,
      variation: cartItem.variation,
      modifiers: cartItem.modifiers || [],
      quantity: cartItem.quantity || 1,
      specialInstructions: cartItem.specialInstructions || "",
    };

    setCartItems((prev) => {
      const existingIndex = prev.findIndex((item) => {
        const sameMenuItem =
          item.menuItem?.id === normalizedCartItem.menuItem?.id;

        const sameVariation =
          (item.variation?.id || null) ===
          (normalizedCartItem.variation?.id || null);

        const itemModifierIds = (item.modifiers || [])
          .map((m) => m.id)
          .sort()
          .join(",");
        const cartModifierIds = (normalizedCartItem.modifiers || [])
          .map((m) => m.id)
          .sort()
          .join(",");
        const sameModifiers = itemModifierIds === cartModifierIds;

        return sameMenuItem && sameVariation && sameModifiers;
      });

      const menuItemName = normalizedCartItem.menuItem?.name || "Item";

      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity:
            updated[existingIndex].quantity + normalizedCartItem.quantity,
        };
        toast.success(`${menuItemName} quantity increased!`);
        return updated;
      } else {
        const newCart = [...prev, normalizedCartItem];
        toast.success(`${menuItemName} added to order!`);
        return newCart;
      }
    });
  };

  const handleUpdateQuantity = (index, newQuantity) => {
    if (newQuantity < 1) return;
    setCartItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, quantity: newQuantity } : item,
      ),
    );
  };

  const handleRemoveItem = (index) => {
    setCartItems((prev) => prev.filter((_, i) => i !== index));
    toast.info("Item removed from order");
  };

  const handleClearCart = () => {
    setCartItems([]);
  };

  // Get all menu items from all categories
  const allMenuItems = useMemo(() => {
    return menuData.flatMap((category) =>
      (category.MenuItems || [])
        .filter((item) => item.isActive)
        .map((item) => ({
          ...item,
          categoryName: category.name,
          categoryId: category.id,
        })),
    );
  }, [menuData]);

  // Use offers from API
  const offerItems = useMemo(() => {
    return offerData
      .filter((item) => item.isActive)
      .map((item) => ({
        ...item,
        categoryName: item.Category?.name || "Special Offers",
        categoryId: item.categoryId,
      }));
  }, [offerData]);

  // Filter menu items based on search, category, and dietary preferences
  const filteredItems = useMemo(() => {
    let items = allMenuItems;

    if (selectedCategory !== "all") {
      items = items.filter((item) => item.categoryId === selectedCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      items = items.filter(
        (item) =>
          item.name.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query),
      );
    }

    if (dietaryFilter.length > 0) {
      items = items.filter((item) => {
        return dietaryFilter.every((filter) => {
          if (filter === "vegetarian") return item.isVegetarian;
          if (filter === "vegan") return item.isVegan;
          if (filter === "spicy") return item.isSpicy;
          return true;
        });
      });
    }

    return items;
  }, [allMenuItems, selectedCategory, searchQuery, dietaryFilter]);

  // Calculate cart total
  const cartTotal = useMemo(() => {
    return cartItems.reduce((total, item) => {
      // Handle potentially nested structure
      const actualMenuItem = item.menuItem?.menuItem || item.menuItem;
      const actualVariation = item.menuItem?.variation || item.variation;
      const actualModifiers = item.modifiers || item.menuItem?.modifiers || [];

      // Get base price with comprehensive fallback
      let basePrice = 0;

      if (
        actualVariation &&
        actualVariation.price !== undefined &&
        actualVariation.price !== null &&
        actualVariation.price !== ""
      ) {
        const varPrice = String(actualVariation.price).replace(/[^0-9.-]/g, "");
        basePrice = parseFloat(varPrice) || 0;
      } else if (
        actualMenuItem?.basePrice !== undefined &&
        actualMenuItem?.basePrice !== null &&
        actualMenuItem?.basePrice !== ""
      ) {
        const bPrice = String(actualMenuItem.basePrice).replace(
          /[^0-9.-]/g,
          "",
        );
        basePrice = parseFloat(bPrice) || 0;
      } else if (
        actualMenuItem?.price !== undefined &&
        actualMenuItem?.price !== null &&
        actualMenuItem?.price !== ""
      ) {
        const itemPrice = String(actualMenuItem.price).replace(/[^0-9.-]/g, "");
        basePrice = parseFloat(itemPrice) || 0;
      }

      // Calculate modifiers price with string cleaning
      const modifiersPrice = actualModifiers.reduce((sum, mod) => {
        const modPriceValue = mod?.price || "0";
        const modPrice =
          parseFloat(String(modPriceValue).replace(/[^0-9.-]/g, "")) || 0;
        return sum + modPrice;
      }, 0);

      // Calculate total for this item
      return total + (basePrice + modifiersPrice) * item.quantity;
    }, 0);
  }, [cartItems]);

  if (!embedded && urlCompanyId && urlTableId) {
    const type = normalizeGuestType(
      urlParams.get("guestType") || urlParams.get("type")
    );
    const target = resolveGuestLandingPath(type, urlTableId);
    return <Navigate to={withGuestSearch(target, location.search)} replace />;
  }

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: useLuxuryMenu
            ? `linear-gradient(180deg, rgba(8,14,12,0.55) 0%, rgba(8,14,12,0.82) 100%), url(${LUXURY.headerImage}) center 38% / cover no-repeat`
            : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        }}
      >
        <CircularProgress size={60} sx={{ color: useLuxuryMenu ? LUXURY.gold : "white", mb: 2 }} />
        <Box
          component="span"
          sx={{
            color: useLuxuryMenu ? LUXURY.cream : "white",
            fontSize: "1.25rem",
            fontWeight: 800,
            letterSpacing: "-0.02em",
          }}
        >
          Loading Menu...
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchMenuData}>
          Retry
        </Button>
      </Container>
    );
  }

  // Count active filters
  const activeFilterCount =
    dietaryFilter.length + (selectedCategory !== "all" ? 1 : 0);

  const handleSubmitCompanyReview = async () => {
    try {
      if (
        ratingForm.customerPhone.trim() &&
        !isValidPhoneNumber(ratingForm.customerPhone)
      ) {
        toast.warning("Please enter a valid phone number (10 to 15 digits)");
        return;
      }

      setRatingSubmitting(true);
      await RestaurantRatingServices.create({
        companyId,
        customerName: ratingForm.customerName.trim(),
        customerPhone: ratingForm.customerPhone.trim() || undefined,
        rating: ratingForm.rating,
        review: ratingForm.review.trim() || undefined,
        isApproved: true,
      });
      toast.success("Thanks for your review! 🎉");
      setRatingForm({
        customerName: "",
        customerPhone: "",
        rating: 0,
        review: "",
        hoverRating: 0,
      });

      const res = await RestaurantRatingServices.getByCompany({
        companyId,
        page: 0,
        limit: 20,
      });
      if (res?.data?.view?.data) setReviews(res.data.view.data);
    } catch {
      toast.error("Failed to submit review. Please try again.");
    } finally {
      setRatingSubmitting(false);
    }
  };

  const cartCustomer = isGuestEmbedded
    ? {
        name:
          guestSession?.customerProfile && !guestSession.customerProfile?.isGuestOnly
            ? guestSession.customerProfile?.name || ""
            : "",
        mobile:
          guestSession?.enteredMobile || guestSession?.customerProfile?.mobile || "",
      }
    : {
        name:
          customerProfile && !customerProfile?.isGuestOnly
            ? customerProfile?.name || ""
            : "",
        mobile: enteredMobile || customerProfile?.mobile || "",
      };

  return (
    <Box
      sx={{
        bgcolor: useLuxuryMenu ? LUXURY.page : "#fafafa",
        backgroundImage: useLuxuryMenu ? LUXURY.pageWash : "none",
        fontFamily: useLuxuryMenu ? LUXURY.bodyFont : "inherit",
        height: "100vh",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Left Column - Header, Filters, Menu Items */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          height: "100vh",
        }}
      >
        <MenuHeaderBar
          luxury={useLuxuryMenu}
          isPublicMode={isPublicMode}
          guestMode={guestMode}
          locationLabel={guestSession?.locationLabel}
          locationSubtitle={guestSession?.table?.location}
          isRoom={guestSession?.isRoom}
          companyData={
            isGuestEmbedded && guestSession?.company
              ? guestSession.company
              : companyData
          }
          onPrimaryNavigation={() => {
            if (isGuestEmbedded) {
              navigate(withGuestSearch("/guest", location.search));
              return;
            }
            navigate(isPublicMode ? "/" : "/dashboard");
          }}
          onDesktopBack={() => {
            if (isGuestEmbedded) {
              navigate(withGuestSearch("/guest", location.search));
              return;
            }
            navigate("/dashboard");
          }}
          onNavigateRestaurants={() => navigate("/restaurants")}
          onOpenCompanyInfo={() => setCompanyInfoOpen(true)}
          onOpenRating={() => setRatingDialogOpen(true)}
          onOpenCart={() => setCartDrawerOpen(true)}
          cartItemCount={cartItems.length}
          customerProfile={isGuestEmbedded ? guestSession?.customerProfile : customerProfile}
          enteredMobile={isGuestEmbedded ? guestSession?.enteredMobile : enteredMobile}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          onClearSearch={() => setSearchQuery("")}
          activeFilterCount={activeFilterCount}
          onOpenMobileFilter={() => setMobileFilterOpen(true)}
        />

        <CategoryNavigationBar
          luxury={useLuxuryMenu}
          offerItems={offerItems}
          onOpenOffers={() => setOffersModalOpen(true)}
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
          menuData={menuData}
          isMobile={isMobile}
          dietaryFilter={dietaryFilter}
          onChangeDietaryFilter={setDietaryFilter}
          onClearAllFilters={() => {
            setDietaryFilter([]);
            setSelectedCategory("all");
            setSearchQuery("");
          }}
        />

        {/* Menu Content - Scrollable */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            px: useLuxuryMenu ? { xs: 2, md: 4 } : { xs: 1.5, md: 4 },
            py: { xs: 2, md: 3 },
            ...(useLuxuryMenu
              ? {
                  background: "transparent",
                }
              : {
                  backgroundImage:
                    "linear-gradient(rgba(255, 255, 255, 0.70), rgba(255, 255, 255, 0.70)), url('/assets/images/BackdropMenu.png')",
                  backgroundRepeat: "repeat",
                  backgroundSize: { xs: "140px auto", md: "220px auto" },
                  backgroundPosition: "top left",
                }),
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              bgcolor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: alpha(useLuxuryMenu ? LUXURY.forest : theme.palette.primary.main, 0.2),
              borderRadius: "10px",
              "&:hover": {
                bgcolor: alpha(useLuxuryMenu ? LUXURY.forest : theme.palette.primary.main, 0.3),
              },
            },
          }}
        >
          <Box sx={useLuxuryMenu ? { maxWidth: { xs: LUXURY.maxWidth, md: "100%" }, mx: "auto" } : undefined}>
          <MenuItemsSection
            luxury={useLuxuryMenu}
            selectedCategory={selectedCategory}
            categories={menuData}
            searchQuery={searchQuery}
            filteredItems={filteredItems}
            onAddToCart={handleAddToCartClick}
            onDirectAdd={handleAddToCartConfirm}
            onResetFilters={() => {
              setSearchQuery("");
              setDietaryFilter([]);
              setSelectedCategory("all");
            }}
          />
          </Box>
        </Box>
      </Box>

      {/* Right Column - Cart Panel (Desktop Only) */}
      {!isMobile && (
        <DesktopCartPanel
          luxury={useLuxuryMenu}
          cartItems={cartItems}
          onClearCart={handleClearCart}
          onRemoveItem={handleRemoveItem}
          onUpdateQuantity={handleUpdateQuantity}
          onOpenCheckout={() => setCartDrawerOpen(true)}
          cartTotal={cartTotal}
          cartAccent={CART_ACCENT}
          cartBtnGradient={CART_BTN_GRADIENT}
          cartBtnGradientHover={CART_BTN_GRADIENT_HOVER}
          cartBtnShadow={CART_BTN_SHADOW}
          cartBtnShadowHover={CART_BTN_SHADOW_HOVER}
        />
      )}

      {/* Floating Cart Button (Mobile) */}
      {isMobile && (
        <FloatingCartButton
          luxury={useLuxuryMenu}
          cartItemCount={cartItems.length}
          onClick={() => setCartDrawerOpen(true)}
        />
      )}

      {/* Mobile Filter Drawer */}
      <MobileFilterDrawer
        luxury={useLuxuryMenu}
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        categories={menuData}
        dietaryFilter={dietaryFilter}
        onDietaryFilterChange={setDietaryFilter}
        onClearFilters={() => {
          setSearchQuery("");
          setDietaryFilter([]);
          setSelectedCategory("all");
        }}
      />

      {/* Cart Drawer (Mobile Checkout) */}
      <CartDrawer
        luxury={useLuxuryMenu}
        open={cartDrawerOpen}
        onClose={() => setCartDrawerOpen(false)}
        cartItems={cartItems}
        onUpdateQuantity={handleUpdateQuantity}
        onRemoveItem={handleRemoveItem}
        onClearCart={handleClearCart}
        initialTableId={urlTableId}
        initialOrderType={urlTableId ? "dine-in" : undefined}
        companyId={companyId}
        guestMode={guestMode}
        locationLabel={guestSession?.locationLabel}
        onOrderPlaced={isGuestEmbedded ? guestSession?.addFoodOrder : undefined}
        initialCustomer={cartCustomer}
      />

      {/* Add to Cart Dialog */}
      <AddToCartDialog
        luxury={useLuxuryMenu}
        open={addToCartDialogOpen}
        onClose={() => {
          setAddToCartDialogOpen(false);
          setSelectedMenuItem(null);
        }}
        item={selectedMenuItem}
        onAddToCart={handleAddToCartConfirm}
        onCompleteOrder={() => {
          setAddToCartDialogOpen(false);
          setSelectedMenuItem(null);
          setCartDrawerOpen(true);
        }}
      />

      <CustomerLookupDialog
        luxury={useLuxuryMenu}
        open={!isGuestEmbedded && customerPromptOpen}
        isMobile={isMobile}
        companyName={companyData?.name}
        mobileValue={customerMobileInput}
        onMobileChange={(value) =>
          setCustomerMobileInput(sanitizePhoneNumber(value).slice(0, 15))
        }
        isMobileInvalid={isCustomerMobileInvalid}
        onSkip={handleSkipCustomerLookup}
        onContinue={handleCustomerLookup}
        isLoading={customerLookupLoading}
        continueDisabled={isCustomerMobileInvalid}
        buttonGradient={CART_BTN_GRADIENT}
        buttonGradientHover={CART_BTN_GRADIENT_HOVER}
        buttonShadow={CART_BTN_SHADOW}
        buttonShadowHover={CART_BTN_SHADOW_HOVER}
      />

      <OffersDialog
        luxury={useLuxuryMenu}
        open={offersModalOpen}
        onClose={() => setOffersModalOpen(false)}
        isMobile={isMobile}
        offerItems={offerItems}
        onAddToCart={(item) => {
          handleAddToCartClick(item);
          setOffersModalOpen(false);
        }}
      />

      {/* Rating Dialog */}
      <RatingDialog
        open={ratingDialogOpen}
        onClose={() => setRatingDialogOpen(false)}
        companyId={companyId}
        restaurantName={companyData?.name}
      />

      <CompanyInfoDialog
        open={companyInfoOpen}
        onClose={() => setCompanyInfoOpen(false)}
        companyData={companyData}
        reviews={reviews}
        reviewsLoading={reviewsLoading}
        ratingForm={ratingForm}
        setRatingForm={setRatingForm}
        isRatingPhoneInvalid={isRatingPhoneInvalid}
        ratingSubmitting={ratingSubmitting}
        onSubmitReview={handleSubmitCompanyReview}
      />
    </Box>
  );
};

export default RestaurantMenu;
